import { useSSO } from "@clerk/clerk-expo";
import type { OAuthStrategy } from "@clerk/types";
import * as Linking from "expo-linking";
import * as WebBrowser from "expo-web-browser";
import { useEffect, useState } from "react";
import { Platform } from "react-native";

WebBrowser.maybeCompleteAuthSession();

type ClerkSSOProps = {
	appId: string;
	tenantId: string;
	onSSOSuccess: () => void;
};

const SSO_ERROR_MESSAGES: Record<string, string> = {
	oauth_access_denied: "Sign-in failed — please contact your administrator",
	oauth_token_exchange_error:
		"Sign-in failed — please contact your administrator",
	not_allowed_access: "Your account is not authorized to access this app",
	oauth_callback_invalid: "Something went wrong — please try again",
	oauth_state_invalid: "Sign-in session expired — please try again",
};

const getSSOErrorMessage = (code: string | undefined): string =>
	(code && SSO_ERROR_MESSAGES[code]) ??
	"Sign-in failed — please contact your administrator";

const useClerkSSO = ({ appId, tenantId, onSSOSuccess }: ClerkSSOProps) => {
	const { startSSOFlow } = useSSO();
	const [isSSOInProgress, setIsSSOInProgress] = useState(false);

	useEffect(() => {
		if (Platform.OS === "web") {
			return;
		}
		WebBrowser.warmUpAsync().catch(() => undefined);
		return () => {
			WebBrowser.coolDownAsync().catch(() => undefined);
		};
	}, []);

	const startOAuthSSO = async (strategy: OAuthStrategy) => {
		const redirectUrl = Linking.createURL("/");
		setIsSSOInProgress(true);
		try {
			const result = await startSSOFlow({
				strategy,
				redirectUrl,
				unsafeMetadata: { appId, tenantId },
			});

			const verificationErrorCode =
				result.signIn?.firstFactorVerification?.error?.code;

			if (!(result.createdSessionId && result.setActive)) {
				throw new Error(getSSOErrorMessage(verificationErrorCode));
			}

			await result.setActive({ session: result.createdSessionId });
			onSSOSuccess();
		} finally {
			setIsSSOInProgress(false);
		}
	};

	return { startOAuthSSO, isSSOInProgress };
};

export default useClerkSSO;
